import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { THEME } from '../../utils/theme';

const ToastNotification = ({ toasts, onDismiss, onAction }) => {
  if (toasts.length === 0) return null;

  const getStatusLabel = (status) => {
    const labels = { played: 'Played', wtp: 'Want to Play', wtf: 'WTF', never: 'Never', unplayed: 'Unplayed' };
    return labels[status] || status;
  };

  const getToastIcon = (type) => {
    const icons = { success: '✓', error: '✕', warning: '⚠', info: 'ℹ' };
    return icons[type] || '•';
  };

  const getToastColor = (type) => {
    const colors = { success: THEME.success, error: THEME.danger, warning: THEME.warning, info: THEME.info };
    return colors[type] || THEME.text;
  };

  const getStatusColor = (status) => {
    const colors = { played: THEME.success, wtp: THEME.warning, wtf: THEME.info, never: THEME.danger, unplayed: THEME.textMuted, shelved: THEME.textSecondary };
    return colors[status] || THEME.text;
  };

  // Auto-dismiss now_playing toasts after 5 seconds
  useEffect(() => {
    const nowPlayingToasts = toasts.filter(t => t.type === 'now_playing');
    nowPlayingToasts.forEach(toast => {
      const timer = setTimeout(() => onDismiss(toast.id), 5000);
      return () => clearTimeout(timer);
    });
  }, [toasts, onDismiss]);

  return (
    <div style={{ position: 'fixed', top: '80px', right: '24px', zIndex: 2000, display: 'flex', flexDirection: 'column', gap: '12px', maxWidth: '400px' }}>
      <AnimatePresence mode="popLayout">
        {toasts.slice(0, 3).map(toast => (
          <motion.div
            key={toast.id}
            layout
            initial={{ opacity: 0, x: 50, scale: 0.9 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: 50, scale: 0.9 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            style={{
              background: toast.type === 'now_playing' ? `linear-gradient(145deg, ${toast.user.color}20, ${THEME.bgCard})`
                : ['success', 'error', 'warning', 'info'].includes(toast.type) ? `linear-gradient(145deg, ${getToastColor(toast.type)}15, ${THEME.bgCard})`
                : `linear-gradient(145deg, ${THEME.bgElevated}, ${THEME.bgCard})`,
              border: `1px solid ${toast.type === 'now_playing' ? toast.user.color + '40'
                : ['success', 'error', 'warning', 'info'].includes(toast.type) ? getToastColor(toast.type) + '40'
                : THEME.border}`,
              borderRadius: '14px',
              padding: '16px',
              boxShadow: `0 8px 32px ${THEME.shadowHeavy}`,
              display: 'flex',
              gap: '12px',
              alignItems: 'flex-start'
            }}
          >
          {/* Simple notification toasts (success, error, warning, info) */}
          {['success', 'error', 'warning', 'info'].includes(toast.type) ? (
            <>
              <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: `${getToastColor(toast.type)}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: getToastColor(toast.type), fontWeight: '700', fontSize: '14px', flexShrink: 0 }}>
                {getToastIcon(toast.type)}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ color: THEME.text, fontSize: '14px', fontWeight: '500' }}>{toast.message}</div>
                {toast.undoFn && (
                  <button
                    onClick={() => { toast.undoFn(); onDismiss(toast.id); }}
                    style={{ background: 'transparent', border: `1px solid ${getToastColor(toast.type)}50`, color: getToastColor(toast.type), borderRadius: '6px', padding: '4px 10px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', marginTop: '8px' }}
                  >
                    {toast.undoLabel || 'Undo'}
                  </button>
                )}
              </div>
            </>
          ) : toast.type === 'now_playing' ? (
            <>
              <div style={{ fontSize: '24px' }}>🎮</div>
              <div style={{ flex: 1 }}>
                <div style={{ color: THEME.text, fontSize: '14px' }}>
                  <span style={{ color: toast.user.color, fontWeight: '600' }}>{toast.user.display_name}</span> started playing{' '}
                  <span style={{ fontWeight: '600' }}>{toast.game_name}</span>
                </div>
              </div>
            </>
          ) : (
            <>
              {toast.game_header_image && (
                <img src={toast.game_header_image} alt="" style={{ width: '60px', height: '34px', objectFit: 'cover', borderRadius: '6px', flexShrink: 0 }} />
              )}
              <div style={{ flex: 1 }}>
                <div style={{ color: THEME.text, fontSize: '14px', marginBottom: '8px' }}>
                  <span style={{ color: toast.user.color, fontWeight: '600' }}>{toast.user.display_name}</span> marked{' '}
                  <span style={{ fontWeight: '600' }}>{toast.game_name}</span> as{' '}
                  <span style={{ color: getStatusColor(toast.details?.status) }}>{getStatusLabel(toast.details?.status)}</span>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={() => onAction(toast, 'match')}
                    style={{
                      background: `linear-gradient(135deg, ${THEME.omega}, ${THEME.supe})`,
                      color: '#000',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '6px 12px',
                      fontSize: '12px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    Mark as {getStatusLabel(toast.details?.status)} too
                  </button>
                  <button
                    onClick={() => onDismiss(toast.id)}
                    style={{
                      background: THEME.bgHover,
                      color: THEME.textMuted,
                      border: 'none',
                      borderRadius: '6px',
                      padding: '6px 12px',
                      fontSize: '12px',
                      cursor: 'pointer'
                    }}
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            </>
          )}
          <button
            onClick={() => onDismiss(toast.id)}
            style={{ background: 'none', border: 'none', color: THEME.textDim, fontSize: '16px', cursor: 'pointer', padding: '0', lineHeight: 1 }}
          >
            &times;
          </button>
        </motion.div>
      ))}
      </AnimatePresence>
    </div>
  );
};

export default ToastNotification;
